<template>
  <div class="dialog-container dialog-light chart-of-accounts">
    <div class="review-excel-dialog">
      <div class="dialog-header">
      <ax-dialog-header
        v-if="typeImport === 'xero'"
        text="Review Xero import"
        :type="'profile'"
        :description="'Below is a summary of your imported Xero data. Take a moment to review and verify the information before proceeding. Entries with no previous or YTD amount will import as hidden by default - toggle visibility for these entries if you wish to have them shown.'"
      ></ax-dialog-header>
      <ax-dialog-header
        v-else
        text="Review Excel Import"
          :type="'import-excel'"
        :description="'Below is a summary of your imported data. Take a moment to review and verify the information before proceeding.'"
      ></ax-dialog-header>
      </div>
      <div class="dialog-body pb-0">
        <div class="mt-5 mb-0">
          <div class="card bg-white rounded-1">
            <div class="card-body">
              <div class="review-import-form">
                <!-- header row -->
                <div class="row header-row data-row">
                  <div class="col-3">
                    <label class="font-weight-bold">Name</label>
                  </div>
                  <div :class="{ 'col-5': xeroColumnDesign, 'col-4': !xeroColumnDesign }" class="row">
                    <div v-if="typeImport === 'excel'" class="col-4">
                      <label class="font-weight-bold">Year Before Last</label>
                    </div>
                    <div :class="typeImport === 'excel' ? 'col-4' : 'col-6'">
                      <label class="font-weight-bold">Previous Year</label>
                    </div>
                    <div :class="typeImport === 'excel' ? 'col-4' : 'col-6'">
                      <label class="font-weight-bold">Year To Date (YTD)</label>
                    </div>
                  </div>
                  <div class="col-3">
                    <label class="font-weight-bold">Type</label>
                  </div>
                  <div class="col-2" v-if="!xeroColumnDesign">
                    <label class="font-weight-bold">Excludes GST</label>
                  </div>
                  <div v-if="xeroColumnDesign" class="col-1 text-center">
                    <label class="font-weight-bold">Show</label>
                  </div>

                </div>
                <!-- data row -->
                <div class="row align-items-center data-row" v-for="detail in data" :key="typeImport === 'xero' ? detail.id : detail.row">
                  <div class="col-3" :class="{ 'text-disabled': detail.hidden }">{{ detail.name }}</div>
                  <div :class="{ 'col-5': xeroColumnDesign, 'col-4': !xeroColumnDesign }" class="row">
                    <div v-if="typeImport === 'excel'" class="col-4" :class="{ 'text-disabled': detail.hidden }">{{ formatCurrency(detail.yearBeforeLastAmount) }}</div>
                    <div :class="[typeImport === 'excel' ? 'col-4' : 'col-6', { 'text-disabled': detail.hidden }]">{{ typeImport === 'xero' ? formatCurrency(detail.previousYear) : formatCurrency(detail.previousYearAmount) }}</div>
                    <div :class="[typeImport === 'excel' ? 'col-4' : 'col-6', { 'text-disabled': detail.hidden }]">{{ typeImport === 'xero' ? formatCurrency(detail.ytd) : formatCurrency(detail.yearToDateAmount) }}</div>
                  </div>
                  <div class="col-3">
                    <k-dropdown-list
                      v-if="typeImport === 'xero'"
                      id="typeSelect"
                      class="k-picker-gray"
                      :label="''"
                      :data-items="types"
                      :text-field="'description'"
                      :data-item-key="'expenseAccountTypeID'"
                      :value-primitive="true"
                      v-model="detail.expenseAccountType"
                      :disabled="detail.disabled"
                    />
                    <div
                      class="data-row-excel"
                      v-if="typeImport === 'excel'"
                      :readonly="true"
                      :disabled="true"
                    >{{ detail.type }}</div>
                  </div>
                  <div class="col-2" v-if="!xeroColumnDesign">
                    <k-checkbox
                      :disabled="!detail.isExpenseCategory || detail.expenseAccountType.expenseAccountTypeID < 3"
                      :default-checked="!detail.includesGST"
                      @change="excludingGST(detail, $event)"
                      size="large" />
                  </div>
                  <div class="col-1 text-center" v-if="xeroColumnDesign">
                    <div
                      class="cursor-pointer"
                      @click="changeAccountVisibility(detail)"
                      :class="cssClassForVisibleIcon(detail)"
                      v-b-tooltip.hover
                      :title="(detail.ableToHide) ? '':'Special accounts, or accounts with data cannot be hidden'">
                      <i class="icon-visibility text-primary"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="dialog-footer">
        <div class="row justify-content-center pt-5">
          <div class="col-12 col-lg-5 text-center">
          <k-button
            variant="secondary"
            class="btn btn-lg btn-secondary w-100 mb-2"
            @click="onNext()"
          >
            Next
          </k-button>
          <k-button
            ref="backBttOfImportExcel"
            class="btn btn-lg btn-outline-primary w-100"
            @click="backClick()"
          >
            Back
          </k-button>
        </div>
      </div>
    </div>

    <!-- Validation Pop up -->
    <ax-shared-popup
      :size="'xl'"
      :id="'reviewModalValidation'"
      ref="reviewModalValidation"
    >
      <template #body>
        <ax-popup
          :popupContent="modalContent"
          :type="'validation'"
          @closePopup="toggleModal(false)"
        ></ax-popup>
      </template>
    </ax-shared-popup>
    <!-- Validation Pop up -->
  </div>
  </div>
</template>

<script>
import BusinessService from "../../services/business-service";
import FormatNumberService from "../../services/format-number-service";
import { useToast } from "vue-toastification";
const toast = useToast();

export default({
  name: "review-import",
  props: {
    typeImport: String,
    data: Array
  },
  data() {
    return {
      types: [],
      modalContent: {
        title: "Import review",
        description: "",
        closeBttText: "Ok",
        hideConfirmBtt: true
      }
    }
  },
  mounted() {
    this.getExpenseAccountTypes();
  },
  computed: {
     xeroColumnDesign: function() {
      return this.typeImport === "xero";
     }
  },
  methods: {
    async getExpenseAccountTypes() {
      await BusinessService.getExpenseAccountTypes()
        .then((data) => {
          this.types = data.types;
        }).catch(this.showAlert);
    },
    validate() {
      var wagesCount = this.data.filter(account => account.expenseAccountType.expenseAccountTypeID === 1 && !account.hidden).length;
      var superCount = this.data.filter(account => account.expenseAccountType.expenseAccountTypeID === 2 && !account.hidden).length;
      var workcoverCount = this.data.filter(account => account.expenseAccountType.expenseAccountTypeID === 4 && !account.hidden).length;
      if (wagesCount === 0) {
        this.showSpecificAlert("Expense account with type 'Wages' is required (and must be visible).");
        return false;
      } else if (superCount === 0) {
        this.showSpecificAlert("Expense account with type 'Super' is required (and must be visible).");
        return false;
      } else if (wagesCount > 1 && this.typeImport !== 'xero') {
        this.showSpecificAlert("Only one expense account with type 'Wages' is allowed.");
        return false;
      } else if (superCount > 1 && this.typeImport !== 'xero') {
        this.showSpecificAlert("Only one expense account with type 'Super' is allowed.");
        return false;
      } else if (workcoverCount > 1) {
        this.showSpecificAlert("Only one expense account with type 'Workcover' is allowed.");
        return false;
      }
      return true;
    },
    toggleModal(show) {
      if (show) {
        this.$refs.reviewModalValidation.open();
      } else {
        this.$refs.reviewModalValidation.close();
      }
    },
    async onNext() {
      var valid = await this.validate();
      if (valid) {
        this.$emit("onNext", this.data);
      }
    },
    async backClick() {
      this.$emit("onBack");
    },
    showSpecificAlert(alert) {
      toast.clear();
      toast.error(alert);
    },
    excludingGST(account, event) {
      this.data.find(x => x.name === account.name && x.row === account.row).includesGST = !event.value;
    },
    formatCurrency(amount) {
      return FormatNumberService.formatNumberResultWithDecimal(amount, 2);
    },
    changeAccountVisibility(accountDetails) {
      if (accountDetails.ableToHide) {
        accountDetails.hidden = !accountDetails.hidden;
      }
    },
    cssClassForVisibleIcon(account) {
      if (account.ableToHide) {
        if (account.hidden) {
          return "opacity-25-visible";
        }
        return "";
      }
      else {
        return "opacity-25 visible";
      }
    },
  }
})
</script>
